import { useState } from "react";
import { Images, CheckCircle2, XCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CHUNK = 8;

export const BulkPhotosDialog = ({ open, onClose, onDone }) => {
  const [files, setFiles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  const pick = async (list) => {
    const arr = [...list].filter((f) => f.type.startsWith("image/"));
    setFiles(arr); setResults(null);
    try { const { data } = await api.post("/fabrics/photos/match", arr.map((f) => f.name)); setMatches(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const matched = matches.filter((m) => m.fabric_id).length;

  const send = async () => {
    const todo = files.filter((f) => matches.find((m) => m.file === f.name)?.fabric_id);
    const all = [];
    setProgress({ done: 0, total: todo.length });
    try {
      for (let i = 0; i < todo.length; i += CHUNK) {
        const fd = new FormData();
        todo.slice(i, i + CHUNK).forEach((f) => fd.append("files", f));
        const { data } = await api.post("/fabrics/photos/bulk", fd);
        all.push(...data.results);
        setProgress({ done: Math.min(i + CHUNK, todo.length), total: todo.length });
      }
      setResults(all); toast.success(`${all.filter((r) => r.status === "ok").length} photo(s) associée(s)`); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setProgress(null); }
  };
  const reset = () => { setFiles([]); setMatches([]); setResults(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-2xl" data-testid="bulk-photos-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Images size={18} /> Photos en masse</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Nommez chaque fichier avec la <b>référence</b> du tissu (ex. <span className="font-mono">WAX01.jpg</span>, <span className="font-mono">wax-01_2.png</span>). Les fichiers sans correspondance sont ignorés.</p>
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-sm hover:bg-muted/50" data-testid="bulk-photos-drop">
          <Upload size={22} className="text-[#059669]" /><span>Choisir des images (plusieurs)</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => pick(e.target.files)} data-testid="bulk-photos-input" />
        </label>
        {files.length > 0 && !results && (
          <div className="space-y-2">
            <p className="text-sm font-semibold" data-testid="bulk-photos-summary">{matched} / {files.length} fichier(s) appariés</p>
            <ul className="max-h-60 space-y-1 overflow-y-auto rounded-md border p-2 text-sm" data-testid="bulk-photos-matches">
              {matches.map((m) => <li key={m.file} className="flex items-center justify-between gap-2"><span className="truncate font-mono text-xs">{m.file}</span>{m.fabric_id ? <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 size={14} /> {m.reference} — {m.name}{m.has_photo && <span className="text-xs text-muted-foreground">(remplace)</span>}</span> : <span className="flex items-center gap-1 text-destructive"><XCircle size={14} /> aucune référence</span>}</li>)}
            </ul>
            <Button className="w-full bg-[#059669] hover:bg-[#047857]" disabled={!matched || !!progress} onClick={send} data-testid="bulk-photos-send">{progress ? `Envoi ${progress.done}/${progress.total}…` : `Envoyer ${matched} photo(s)`}</Button>
          </div>
        )}
        {results && (
          <ul className="max-h-60 space-y-1 overflow-y-auto rounded-md border p-2 text-sm" data-testid="bulk-photos-results">
            {results.map((r) => <li key={r.file} className="flex items-center justify-between gap-2"><span className="truncate font-mono text-xs">{r.file}</span><span className={r.status === "ok" ? "text-emerald-700" : "text-destructive"}>{r.status === "ok" ? `✓ ${r.reference}` : `${r.status} : ${r.detail}`}</span></li>)}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
